import React, { useMemo, useRef, useState } from "react";
import { formatNumber } from "../../../../shared/utils/numberFormatter";

/**
 * FlowVisualization — dark-themed Sankey-style flow diagram.
 *
 * Visual spec:
 *  - Dark background (#0f1117)
 *  - Column headers as chevron pills: "A - 1", "A event_name", "A + 1", "B - 1" …
 *  - Nodes are plain vertical rectangles (NO text inside)
 *  - Labels appear ABOVE each node: icon · event name (bold), then "pct%  count" below
 *  - Active event nodes: #8979FF (purple)
 *  - Others / Drop-off nodes: #6b7280 (grey)
 *  - Curved bezier links between adjacent columns, semi-transparent
 */

// ── Constants ────────────────────────────────────────────────────────────────

const NODE_COLOR   = "#8979FF";
const GREY_COLOR   = "#9ca3af";
const BG_COLOR     = "transparent";
const HEADER_H     = 44;    // px for chevron header row
const LABEL_H      = 38;    // px per node for the text above each rect (name + stats)
const NODE_GAP     = 12;    // vertical gap between nodes in same column
const COL_W        = 120;   // width of each node rectangle
const COL_GAP      = 120;   // horizontal gap between columns
const PAD_H        = 48;    // left/right SVG padding
const PAD_V        = 16;    // top/bottom padding below header
const MIN_RECT_H   = 20;    // minimum rectangle height
const MAX_RECT_H   = 200;   // maximum rectangle height
const AVAILABLE_H  = 400;   // usable height for node rects (before dynamic adjustment)

// Letter label per anchor index
function anchorLetter(idx) {
  return String.fromCharCode(65 + idx); // A, B, C…
}

// Build column header label
function colHeaderLabel(step, anchors) {
  const anchor = anchors.find((a) => a.step === step);
  if (anchor) {
    const letter = anchorLetter(anchors.indexOf(anchor));
    const shortName = anchor.eventName.length > 16 ? anchor.eventName.slice(0, 15) + "…" : anchor.eventName;
    return { label: `${letter}  ${shortName}`, isAnchor: true, letter };
  }
  // Find nearest anchor to determine letter and direction
  const sorted = [...anchors].sort((a, b) => Math.abs(a.step - step) - Math.abs(b.step - step));
  const nearest = sorted[0];
  if (!nearest) return { label: String(step), isAnchor: false, letter: "A" };
  const letter = anchorLetter(anchors.indexOf(nearest));
  const diff = step - nearest.step;
  const label = diff < 0 ? `${letter}  -  ${Math.abs(diff)}` : `${letter}  +  ${diff}`;
  return { label, isAnchor: false, letter };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FlowVisualization({ data, height }) {
  const svgRef = useRef(null);
  const [linkTooltip, setLinkTooltip] = useState(null);

  const { nodes = [], links = [], anchors = [] } = data || {};

  const layout = useMemo(() => {
    if (!nodes.length) return null;

    // Group nodes by step
    const stepMap = new Map();
    for (const node of nodes) {
      if (!stepMap.has(node.step)) stepMap.set(node.step, []);
      stepMap.get(node.step).push(node);
    }

    const sortedSteps = [...stepMap.keys()].sort((a, b) => a - b);
    const numCols = sortedSteps.length;
    const svgWidth = PAD_H * 2 + numCols * COL_W + (numCols - 1) * COL_GAP;

    // Per-column: sort nodes (anchor first, then by count desc, Others/Dropoff last)
    const sortedColNodes = new Map();
    for (const step of sortedSteps) {
      const colNodes = stepMap.get(step) || [];
      sortedColNodes.set(
        step,
        [...colNodes].sort((a, b) => {
          if (a.isAnchor && !b.isAnchor) return -1;
          if (!a.isAnchor && b.isAnchor) return 1;
          if ((a.isDropoff || a.isOthers) && !b.isDropoff && !b.isOthers) return 1;
          if (!a.isDropoff && !a.isOthers && (b.isDropoff || b.isOthers)) return -1;
          return b.count - a.count;
        })
      );
    }

    // Compute column X positions
    const colX = new Map(); // step → x of left edge of node rect
    for (let ci = 0; ci < sortedSteps.length; ci++) {
      colX.set(sortedSteps[ci], PAD_H + ci * (COL_W + COL_GAP));
    }

    // Compute per-node layout
    // Each node occupies: LABEL_H (text above) + rectH (the rect itself) + NODE_GAP
    const nodeLayout = new Map(); // node.id → { x, labelY, rectY, rectH, color }

    // Find max count per column for proportional rect heights
    for (const step of sortedSteps) {
      const colNodes = sortedColNodes.get(step);
      const x = colX.get(step);

      // Total count in column (for proportional distribution)
      const totalCount = colNodes.reduce((s, n) => s + n.count, 0) || 1;

      // Compute rect heights proportionally
      const rectHeights = colNodes.map((n) => {
        const raw = (n.count / totalCount) * AVAILABLE_H;
        return Math.max(MIN_RECT_H, Math.min(MAX_RECT_H, Math.round(raw)));
      });

      // Stack from top, starting after header + padding
      let y = HEADER_H + PAD_V;
      for (let ni = 0; ni < colNodes.length; ni++) {
        const node = colNodes[ni];
        const rectH = rectHeights[ni];

        const color =
          node.isAnchor ? NODE_COLOR
          : node.isDropoff ? GREY_COLOR
          : node.isOthers ? GREY_COLOR
          : NODE_COLOR;

        nodeLayout.set(node.id, {
          x,
          labelY: y,       // top of label area
          rectY: y + LABEL_H, // top of rect
          rectH,
          color,
          node,
        });

        y += LABEL_H + rectH + NODE_GAP;
      }
    }

    // Compute dynamic SVG height from tallest column
    let maxBottom = HEADER_H + PAD_V;
    for (const [, item] of nodeLayout) {
      maxBottom = Math.max(maxBottom, item.rectY + item.rectH + PAD_V);
    }
    const svgHeight = maxBottom + PAD_V;

    // Build link layout — connect right edge of source to left edge of target
    // We need to track vertical offset within each node as links stack
    const srcOffsets = new Map(); // nodeId → current y offset used on right edge
    const tgtOffsets = new Map(); // nodeId → current y offset used on left edge
    for (const [id, item] of nodeLayout) {
      srcOffsets.set(id, item.rectY);
      tgtOffsets.set(id, item.rectY);
    }

    const linkLayout = [];
    // Sort links by source node then target (for consistent stacking)
    const sortedLinks = [...links].sort((a, b) => {
      const sA = nodeLayout.get(a.source);
      const sB = nodeLayout.get(b.source);
      return (sA?.rectY || 0) - (sB?.rectY || 0);
    });

    for (const link of sortedLinks) {
      const src = nodeLayout.get(link.source);
      const tgt = nodeLayout.get(link.target);
      if (!src || !tgt) continue;

      const srcCount = src.node.count || 1;
      const tgtCount = tgt.node.count || 1;

      const srcH = Math.max(2, Math.round((link.value / srcCount) * src.rectH));
      const tgtH = Math.max(2, Math.round((link.value / tgtCount) * tgt.rectH));

      const srcY1 = srcOffsets.get(link.source);
      const tgtY1 = tgtOffsets.get(link.target);

      srcOffsets.set(link.source, srcY1 + srcH);
      tgtOffsets.set(link.target, tgtY1 + tgtH);

      linkLayout.push({
        link,
        x1: src.x + COL_W,   // right edge of source
        y1: srcY1,
        h1: srcH,
        x2: tgt.x,            // left edge of target
        y2: tgtY1,
        h2: tgtH,
        value: link.value,
      });
    }

    // Column header info
    const headers = sortedSteps.map((step) => ({
      step,
      x: colX.get(step),
      ...colHeaderLabel(step, anchors),
    }));

    return { nodeLayout, linkLayout, sortedSteps, colX, svgWidth, svgHeight, headers };
  }, [nodes, links, anchors]);

  if (!data || !nodes.length) {
    return (
      <div
        className="flex items-center justify-center text-sm"
        style={{ height: height || 400, color: "#6b7280" }}
      >
        No flow data. Select an event and run the query.
      </div>
    );
  }

  if (!layout) return null;

  const { nodeLayout, linkLayout, svgWidth, svgHeight, headers } = layout;

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderLink = (item, idx) => {
    const { x1, y1, h1, x2, y2, h2, value } = item;
    const cx = (x1 + x2) / 2;

    // Top bezier edge
    const pathTop = `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
    // Bottom bezier edge
    const pathBot = `M ${x1} ${y1 + h1} C ${cx} ${y1 + h1}, ${cx} ${y2 + h2}, ${x2} ${y2 + h2}`;

    const d = `${pathTop} L ${x2} ${y2 + h2} C ${cx} ${y2 + h2}, ${cx} ${y1 + h1}, ${x1} ${y1 + h1} Z`;

    return (
      <path
        key={idx}
        d={d}
        fill="rgba(137,121,255,0.18)"
        stroke="none"
        className="transition-opacity hover:opacity-60 cursor-pointer"
        onMouseEnter={(e) => {
          const rect = svgRef.current?.getBoundingClientRect();
          if (rect) {
            setLinkTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, value });
          }
        }}
        onMouseLeave={() => setLinkTooltip(null)}
      />
    );
  };

  const renderNode = ([id, item]) => {
    const { x, labelY, rectY, rectH, color, node } = item;
    const name = node.eventName.length > 18 ? node.eventName.slice(0, 17) + "…" : node.eventName;
    const pct = node.percentage != null ? `${node.percentage}%` : "";
    const countStr = formatNumber(node.count);

    // Icon: small circle dot or special icon
    const iconColor = node.isDropoff || node.isOthers ? GREY_COLOR : NODE_COLOR;
    const iconY = labelY + 10;
    const textX = x + 14;

    return (
      <g key={id}>
        {/* Small icon dot */}
        <circle cx={x + 5} cy={iconY} r={4} fill={iconColor} opacity={0.85} />

        {/* Event name */}
        <text
          x={textX}
          y={iconY + 1}
          dominantBaseline="middle"
          fontSize={11}
          fontWeight={700}
          fill="#1e293b"
          fontFamily="Raleway, sans-serif"
        >
          {name}
        </text>

        {/* Pct + count */}
        <text
          x={textX}
          y={labelY + 26}
          dominantBaseline="middle"
          fontSize={10}
          fill="#475569"
          fontFamily="Raleway, sans-serif"
        >
          {pct}
          {"  "}
          <tspan fill="#64748b">{countStr}</tspan>
        </text>

        {/* Node rectangle — no text inside */}
        <rect
          x={x}
          y={rectY}
          width={COL_W}
          height={rectH}
          rx={4}
          fill={color}
          opacity={node.isAnchor ? 1 : 0.85}
        />
      </g>
    );
  };

  const renderHeader = (hdr) => {
    const cx = hdr.x + COL_W / 2;
    const pillW = Math.min(COL_W + 20, Math.max(COL_W, hdr.label.length * 6.5 + 24));
    const pillX = cx - pillW / 2;
    const pillH = 28;
    const pillY = 8;

    return (
      <g key={`hdr-${hdr.step}`}>
        {/* Pill background */}
        <rect
          x={pillX}
          y={pillY}
          width={pillW}
          height={pillH}
          rx={14}
          fill={hdr.isAnchor ? "#ede9fe" : "#f1f5f9"}
        />
        {/* Column step label */}
        <text
          x={cx}
          y={pillY + pillH / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={hdr.isAnchor ? 11 : 10}
          fontWeight={hdr.isAnchor ? 700 : 500}
          fill={hdr.isAnchor ? "#5b21b6" : "#374151"}
          fontFamily="Raleway, sans-serif"
        >
          {hdr.label}
        </text>
      </g>
    );
  };

  const svgH = height || svgHeight;

  return (
    <div
      className="relative w-full overflow-x-auto"
      style={{ minHeight: svgH }}
    >
      <svg
        ref={svgRef}
        width={Math.max(svgWidth, 600)}
        height={svgHeight}
        style={{ display: "block" }}
      >
        {/* Links drawn first (behind nodes) */}
        {linkLayout.map(renderLink)}

        {/* Nodes */}
        {[...nodeLayout.entries()].map(renderNode)}

        {/* Column headers */}
        {headers.map(renderHeader)}
      </svg>

      {/* Link tooltip */}
      {linkTooltip && (
        <div
          className="pointer-events-none absolute z-50 px-2.5 py-1.5 rounded-lg shadow-lg text-xs whitespace-nowrap"
          style={{
            left: linkTooltip.x + 10,
            top: linkTooltip.y - 10,
            background: "rgba(15,23,42,0.95)",
            color: "#f1f5f9",
            border: "1px solid #334155",
          }}
        >
          {formatNumber(linkTooltip.value)} users
        </div>
      )}
    </div>
  );
}
